import AsyncStorage from '@react-native-async-storage/async-storage';

import { queryClient } from '@/data';
import { unregisterNotification } from '@/data/braze';
import { URLS, getAuthRedirectURI } from '@/data/config';
import { closeIntercom } from '@/data/intercom/intercom';
import { getGeocomplyClient } from '@/data/location/geocomply-common';
import { EVENT_TYPES, RUDDERSTACK_EVENTS } from '@/feature/analytics/constants';
import { notifyRudderStackUserLogin, trackRudderStackEvent } from '@/feature/analytics/trackRudderStackEvent';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { filteredEntriesKeys } from '@/feature/entries-pickem/hooks/use-infinite-filtered-entries';
import { useLinkingStore } from '@/feature/entry-share/hooks/use-linking';
import { casinoFavoritesKeys } from '@/feature/lobby-casino/hooks/use-casino-favorites';
import { endRipsSession } from '@/feature/lobby-rips/hooks/use-rips-session';
import { usePromotionsStore } from '@/feature/promohub/hooks/use-promotions-store';
import { userLimitsKeys } from '@/feature/responsible-gaming/hooks/query-keys';
import { authUserKeys } from '@/hooks/use-auth-user-config';
import { userPromosKeys } from '@/providers/BonusEngineClientProvider';
import { ripsKeys } from '@/providers/TradingCardsClientProvider';
import { clearAllKeysWithExceptions as asyncStorageClearAllKeysWithExceptions, read } from '@/utils/async-storage';
import { isWeb } from '@/utils/constants-platform-specific';
import { setCookie } from '@/utils/cookies';
import { logger } from '@/utils/logging';
import { runLogoutListeners } from '@/utils/logout-listeners';
import { jwtDecode } from 'jwt-decode';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { useJurisdictionStore } from './use-jurisdiction';
import { useWalletStore } from './use-wallet';

const DAY_IN_SECONDS = 86400;

const TAG = '[User]';

export type UserSession = {
    access_token?: string;
    refresh_token?: string;
    refresh_expires_in?: number;
    expires_in?: number;
    expires_at?: number;
    sub?: string;
    error?: unknown;
} | null;

export type UserProfile = {
    avatar_url: string;
    username: string;
    email: string;
    email_verified?: string;
    face_id_enabled?: boolean;
    face_id_verified?: boolean;
    family_name: string;
    given_name: string;
    mfa?: boolean;
    mobile_number: string;
    mobile_number_verified?: boolean;
    name?: string;
    preferred_username?: string;
    promo_code?: string;
    sub: string;
};

/**
 * Tracks user login and registration events using RudderStack.
 *
 * @param userProfile - The profile of the user to be tracked.
 * @param isWebRegister - Optional flag indicating if the event is a registration event.
 *
 * @remarks
 * - On user login, it sends an identify event to RudderStack.
 * - On user registration, it tracks a registration success event and includes UTM parameters and promo code if available.
 */
const rudderStackTracking = async (userProfile: UserProfile, isWebRegister?: boolean) => {
    if (userProfile) {
        notifyRudderStackUserLogin({
            userId: userProfile.sub,
            email: userProfile.email,
        });

        if (isWebRegister) {
            const utmParameters: Record<string, string> | undefined = await read('utm_parameters');
            const promoCode: string | undefined = await read('promo_code');
            trackRudderStackEvent(RUDDERSTACK_EVENTS.trigger('Registration Success'), {
                eventType: EVENT_TYPES.REGISTRATION,
                promoCode,
                ...utmParameters,
            });
        }
    }
};

export type UrlsConfigType = {
    token_endpoint: string;
    end_session_endpoint: string;
};

export interface UserStore {
    configs: { scheme: string; urls: UrlsConfigType | null };
    loading: boolean;
    _hydrated: boolean;
    guest: boolean;
    session: UserSession | null;
    profile: UserProfile;
    actions: {
        setSession: (session: UserSession) => UserSession;
        sessionHasExpired: () => boolean;
        getConfigs: () => Promise<UrlsConfigType>;
        setHydrated: () => void;
        getSession: () => Promise<UserSession>;
        authorize: (code: string, isWebRegister?: boolean) => Promise<void>;
        loadProfile: (session?: UserSession) => UserProfile | null;
        refresh: (userSession: UserSession) => Promise<UserSession>;
        logout: () => Promise<void>;
        loadPreviousSession: () => Promise<UserStore>;
        setupGuestSession: () => void;
        removeGuestSession: () => void;
        updateProfile: (partial: Partial<UserProfile>) => void;
    };
}

export const useUserStore = create<UserStore>()(
    persist(
        (set, get) => ({
            configs: { scheme: 'betrpickem://auth', urls: null },
            loading: false,
            _hydrated: false,
            guest: true,
            session: null,
            profile: {} as UserProfile,
            actions: {
                setSession: (session: UserSession): UserSession => {
                    if (!session) {
                        queryClient.removeQueries({ queryKey: ripsKeys.all });
                        set({ session: null });
                        return null;
                    }

                    const currentSession = get().session;
                    const newSession: UserSession = {
                        ...(currentSession || {}),
                        ...session,
                        ...(session.expires_in
                            ? {
                                  expires_at:
                                      typeof session.expires_at !== 'undefined'
                                          ? session.expires_at
                                          : Date.now() + session.expires_in * 1e3,
                              }
                            : {}),
                    };

                    set({ guest: false, session: newSession });

                    logger.info('USER: SET', new Date(newSession?.expires_at ?? 0), {
                        access_token: newSession?.access_token?.slice(-20),
                        expires_in: newSession?.expires_in,
                        expires_at: newSession?.expires_at,
                        refresh_token: newSession?.refresh_token?.slice(-20),
                        refresh_expires_in: newSession?.refresh_expires_in,
                    });

                    return newSession;
                },

                sessionHasExpired: (): boolean => {
                    if (!get()._hydrated) {
                        logger.debug('Calling sessionHasExpired before user being hydrated!');
                    }
                    const session = get().session;
                    return !session || (session?.expires_at ?? 0) <= Date.now();
                },

                getConfigs: async () => {
                    const configs = get().configs;
                    if (!configs.urls) {
                        const urls = await (await fetch(URLS.KEYCLOAK_AUTH_REDIRECT_URL)).json();
                        set({ configs: { ...configs, urls } });
                        return urls;
                    }
                    return configs.urls;
                },

                setHydrated: () => {
                    set({ _hydrated: true });
                },

                getSession: async () => {
                    if (!get().actions.sessionHasExpired()) {
                        return get().session;
                    }

                    const saved = get().session;

                    if (saved?.access_token && !saved?.error) {
                        get().actions.setSession(saved);

                        if (get().actions.sessionHasExpired()) {
                            logger.debug(TAG, 'getSession', 'Session is expired. Refresh...');
                            return await get().actions.refresh(saved);
                        }
                    }

                    if (!get().profile?.sub) {
                        logger.debug(TAG, 'getSession', 'loadProfile()');
                        get().actions.loadProfile(saved);
                    }

                    return get().session;
                },

                authorize: async (code: string, isWebRegister?: boolean) => {
                    set({ loading: true });
                    const configs = await get().actions.getConfigs();

                    const body = new URLSearchParams();
                    body.append('grant_type', 'authorization_code');
                    body.append('client_id', URLS.KEYCLOAK_CLIENT_ID);
                    body.append('scope', 'openid offline_access');
                    body.append('code', code);
                    body.append('redirect_uri', getAuthRedirectURI(isWebRegister));

                    const session = await (
                        await fetch(configs.token_endpoint, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/x-www-form-urlencoded',
                            },
                            body: body.toString(),
                        })
                    ).json();

                    logger.debug(TAG, 'User Session: Authorized');
                    get().actions.setSession(session);

                    get().actions.loadProfile(session);

                    rudderStackTracking(get().profile, isWebRegister);

                    set({ loading: false });
                },

                loadProfile: (session?: UserSession): UserProfile | null => {
                    const token = session?.access_token ?? get().session?.access_token;
                    if (!token) {
                        return null;
                    }
                    set({ guest: false });
                    try {
                        // cookie used on the promo page
                        setCookie('access_token', token, DAY_IN_SECONDS, '.betr.app', 'None');
                        const decodedJWT = jwtDecode(token);
                        const newProfile = { ...get().profile, ...decodedJWT } as UserProfile;
                        set({ profile: newProfile });
                    } catch (e) {
                        logger.error(TAG, 'loadProfile', e);
                    }

                    return get().profile;
                },

                refresh: async (userSession: UserSession) => {
                    const configs = await get().actions.getConfigs();

                    const body = new URLSearchParams();
                    body.append('grant_type', 'refresh_token');
                    body.append('client_id', URLS.KEYCLOAK_CLIENT_ID);
                    body.append('scope', 'openid offline_access');
                    body.append('refresh_token', userSession?.refresh_token ?? '');

                    try {
                        const session = await (
                            await fetch(configs?.token_endpoint, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/x-www-form-urlencoded',
                                    Authorization: `Bearer ${userSession?.access_token}`,
                                },
                                body: body.toString(),
                            })
                        ).json();

                        if (session.error) {
                            logger.error('[auth] User Session: Error', session);
                            return get().actions.setSession(null);
                        }

                        get().actions.loadProfile(session);

                        return get().actions.setSession(session);
                    } catch (e) {
                        logger.error(TAG, 'refresh session failed.', { e });
                    }

                    return get().session;
                },

                logout: async () => {
                    await endRipsSession();
                    const productSettings =
                        useJurisdictionStore.getState().jurisdictionSettings?.productConfig?.settings;
                    const guestAllowed = productSettings?.guest_session_enabled ?? false;
                    set({ loading: true, guest: guestAllowed, profile: {} as UserProfile });
                    const configs = await get().actions.getConfigs();
                    const token = get().session?.access_token;

                    const body = new URLSearchParams();
                    body.append('client_id', URLS.KEYCLOAK_CLIENT_ID);
                    body.append('refresh_token', get().session?.refresh_token ?? '');

                    try {
                        await (
                            await fetch(configs?.end_session_endpoint, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/x-www-form-urlencoded',
                                    authorization: `Bearer ${token}`,
                                },
                                body: body.toString(),
                            })
                        ).text();
                    } catch (e) {
                        logger.error(TAG, 'User Session: Logout', e);
                    }

                    get().actions.setSession(null);
                    await queryClient.resetQueries({ queryKey: authUserKeys.all });

                    queryClient.removeQueries({ queryKey: userLimitsKeys.all });

                    queryClient.removeQueries({ queryKey: userPromosKeys.all });

                    queryClient.removeQueries({ queryKey: filteredEntriesKeys.all });

                    queryClient.removeQueries({ queryKey: casinoFavoritesKeys.all });

                    useBetslipStore.getState().actions.clearBetslip();

                    runLogoutListeners();

                    closeIntercom();

                    useLinkingStore.getState().clearHandledSources();

                    usePromotionsStore.getState().actions.clearDepositMatchPromo();

                    set({ loading: false });

                    getGeocomplyClient().shutdownAndLogout();

                    await asyncStorageClearAllKeysWithExceptions();

                    if (isWeb) {
                        // unsubscribe a web user from Braze's notification on logout
                        unregisterNotification();
                    }
                },

                loadPreviousSession: async () => {
                    await get().actions.getSession();
                    logger.debug(TAG, 'loaded previous session and user info.', get().profile.sub);
                    return get();
                },

                setupGuestSession: () => {
                    set({ guest: true });
                    //Setup a fake wallet of 700$ for guest users
                    useWalletStore.getState().actions.setupGuestWallet({
                        real_amount: '700',
                        total: '700',
                        bonus_amount: '0',
                        free_amount: '0',
                        real_currency: 'USD',
                    });
                },

                removeGuestSession: () => {
                    set({ guest: false });
                },

                updateProfile: (partial: Partial<UserProfile>) => {
                    set(state => ({ profile: { ...state.profile, ...partial } }));
                },
            },
        }),
        {
            name: 'user-session-storage',
            version: 1,
            storage: createJSONStorage(() => AsyncStorage),
            partialize: (state: UserStore) => ({
                session: state.session?.error ? null : state.session,
            }),
            onRehydrateStorage: () => state => {
                if (state?.session?.access_token && !state.session.error) {
                    state.actions.setSession(state.session);
                    //Load the user profile as part of the hydration step
                    state.actions.loadProfile();
                }
                state?.actions.setHydrated();
            },
        }
    )
);

export const isProfileLoadedSelector = (state: UserStore) => !!state.profile?.sub;
